import {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
  type ReactElement,
  type UIEvent,
} from 'react'
import { compareDisplay, displaySatisfies, parseFilter } from './filterGrammar'
import { SuggestionPopup, type Suggestion } from './SuggestionPopup'
import type {
  RecordColumn,
  RecordFilter,
  RecordSearchParams,
  SelectRecordProps,
  RecordSortDir,
} from './types'

const DEFAULT_PAGE_SIZE = 20
const SCROLL_THRESHOLD_PX = 120
const FILTER_DEBOUNCE_MS = 300
const SUGGESTION_LIMIT = 10
const HEADER_ROW_HEIGHT = 38 // filter row sticks directly under the header row
const DEFAULT_COL_WIDTH = 140 // px, for columns that don't specify a width

// Persisted sort (key + direction only — never the filters), keyed by the caller's `slug`.
type PersistedSort<T> = { sortKey: keyof T | null; sortDir: RecordSortDir }

function readPersistedSort<T>(slug: string, columns: RecordColumn<T>[]): PersistedSort<T> | null {
  try {
    const raw = localStorage.getItem(`select-record-${slug}`)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { sortKey?: unknown; sortDir?: unknown }
    const sortDir: RecordSortDir = parsed.sortDir === -1 ? -1 : 1
    if (parsed.sortKey == null) return { sortKey: null, sortDir }
    // Only honor a still-present, still-sortable column.
    const col = columns.find((c) => String(c.key) === parsed.sortKey && c.sortable !== false)
    return col ? { sortKey: col.key, sortDir } : null
  } catch {
    return null
  }
}

function writePersistedSort<T>(
  slug: string,
  sortKey: keyof T | null,
  sortDir: RecordSortDir,
): void {
  try {
    localStorage.setItem(
      `select-record-${slug}`,
      JSON.stringify({ sortKey: sortKey === null ? null : String(sortKey), sortDir }),
    )
  } catch {
    // Ignore storage failures (private mode, quota, SSR) — persistence is best-effort.
  }
}

// Mutable query + paging state, mirrored in a ref so the async fetch, scroll handler, and debounced
// reload all read the latest values without stale closures (a faithful port of the reference demo).
type InternalState<T> = {
  filtersRaw: Record<string, string> // column key → raw input text
  globalRaw: string
  sortKey: keyof T | null
  sortDir: RecordSortDir
  rows: T[]
  total: number
  incompleteTotal: boolean // `total` is a lower bound → render with a trailing "+"
  offset: number // next page offset to request
  hasMore: boolean
  loading: boolean
  error: boolean
  selectedId: string | null
  suggestKey: string | null // column key whose suggestion popup is open
}

function displayOf<T>(column: RecordColumn<T>, row: T): string {
  return column.display ? column.display(row) : String(row[column.key] ?? '')
}

export function SelectRecord<T>(props: SelectRecordProps<T>): ReactElement {
  const {
    title,
    columns,
    getRowId,
    onSelect,
    slug,
    onEscape,
    initialGlobalSearch,
    pageSize = DEFAULT_PAGE_SIZE,
    recordNoun = 'record',
    globalSearchPlaceholder = 'Search all columns…',
    showGlobalSearch = true,
    showSelectionBar = true,
    footerHint,
    renderSelectionSummary,
    className,
  } = props

  // Props read inside stable callbacks — kept in a ref so those callbacks never need to be recreated.
  const propsRef = useRef({ search: props.search, columns, pageSize, slug })
  propsRef.current = { search: props.search, columns, pageSize, slug }

  // Restore the persisted sort once (never the filters). Lazy init so localStorage is read a single
  // time, not on every render.
  const [initialSort] = useState(() => (slug ? readPersistedSort<T>(slug, columns) : null))

  const stateRef = useRef<InternalState<T>>({
    filtersRaw: {},
    globalRaw: initialGlobalSearch ?? '',
    sortKey: initialSort?.sortKey ?? null,
    sortDir: initialSort?.sortDir ?? 1,
    rows: [],
    total: 0,
    incompleteTotal: false,
    offset: 0,
    hasMore: true,
    loading: false,
    error: false,
    selectedId: null,
    suggestKey: null,
  })
  const [, bump] = useReducer((n: number) => n + 1, 0)

  const tokenRef = useRef(0)
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const hideTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const filterInputRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const buildFilters = useCallback((): RecordFilter<T>[] => {
    const { columns: cols } = propsRef.current
    const st = stateRef.current
    const out: RecordFilter<T>[] = []
    for (const col of cols) {
      if (col.filterable === false) continue
      const parsed = parseFilter(st.filtersRaw[String(col.key)])
      if (!parsed) continue
      out.push({ field: col.key, operator: parsed.operator, operand: parsed.operand })
    }
    return out
  }, [])

  // reset=true → filters/order/search changed: discard rows and fetch page 1.
  // reset=false → infinite scroll: fetch the next page and append.
  const loadPage = useCallback(
    (reset: boolean) => {
      const st = stateRef.current
      if (reset) {
        st.offset = 0
        st.rows = []
        st.hasMore = true
        st.error = false
        st.loading = false // supersede any in-flight request (its token goes stale below)
      }
      if (st.loading || !st.hasMore) return
      st.loading = true
      const token = ++tokenRef.current
      bump() // reflect the loading state immediately

      const params: RecordSearchParams<T> = {
        filters: buildFilters(),
        globalSearch: st.globalRaw,
        sortKey: st.sortKey,
        sortDir: st.sortDir,
        offset: st.offset,
        limit: propsRef.current.pageSize,
      }

      propsRef.current
        .search(params)
        .then((res) => {
          if (token !== tokenRef.current) return // a newer query superseded this one
          st.rows = st.rows.concat(res.rows)
          st.total = res.total
          st.incompleteTotal = res.incompleteTotal ?? false
          st.offset += res.rows.length
          st.hasMore = res.hasMore
          st.loading = false
          bump()
        })
        .catch(() => {
          if (token !== tokenRef.current) return
          st.loading = false
          st.hasMore = false
          st.error = true
          bump()
        })
    },
    [buildFilters],
  )

  const reloadImmediate = useCallback(() => {
    clearTimeout(debounceTimer.current)
    loadPage(true)
  }, [loadPage])

  const reloadDebounced = useCallback(() => {
    clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => loadPage(true), FILTER_DEBOUNCE_MS)
  }, [loadPage])

  // First page on mount.
  useEffect(() => {
    loadPage(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Close the suggestion popup on window resize (position is captured on open).
  useEffect(() => {
    const close = () => {
      if (stateRef.current.suggestKey !== null) {
        stateRef.current.suggestKey = null
        bump()
      }
    }
    window.addEventListener('resize', close)
    return () => window.removeEventListener('resize', close)
  }, [])

  const closeSuggestions = useCallback(() => {
    clearTimeout(hideTimer.current)
    if (stateRef.current.suggestKey !== null) {
      stateRef.current.suggestKey = null
      bump()
    }
  }, [])

  // ---- Sorting: ascending → descending → unsorted, one column at a time. ----
  const toggleSort = useCallback(
    (key: keyof T) => {
      const st = stateRef.current
      if (st.sortKey !== key) {
        st.sortKey = key
        st.sortDir = 1
      } else if (st.sortDir === 1) {
        st.sortDir = -1
      } else {
        st.sortKey = null
        st.sortDir = 1
      }
      if (propsRef.current.slug) {
        writePersistedSort(propsRef.current.slug, st.sortKey, st.sortDir)
      }
      reloadImmediate()
    },
    [reloadImmediate],
  )

  // ---- Selection ----
  const select = useCallback(
    (row: T) => {
      stateRef.current.selectedId = getRowId(row)
      bump()
      onSelect(row)
    },
    [getRowId, onSelect],
  )

  // ---- Per-column suggestions, computed client-side from the loaded rows. ----
  const computeSuggestions = useCallback(
    (key: string): { items: Suggestion[]; hasMore: boolean } => {
      const st = stateRef.current
      const col = propsRef.current.columns.find((c) => String(c.key) === key)
      if (!col) return { items: [], hasMore: false }
      const parsed = parseFilter(st.filtersRaw[key])
      const useOrder = parsed?.operator === 'gt' || parsed?.operator === 'lt'
      const counts = new Map<string, number>()
      for (const row of st.rows) {
        const v = displayOf(col, row)
        if (v === '') continue
        if (parsed) {
          const ok = useOrder
            ? displaySatisfies(v, parsed.operator, parsed.operand)
            : v.toLowerCase().includes(parsed.operand.toLowerCase())
          if (!ok) continue
        }
        counts.set(v, (counts.get(v) ?? 0) + 1)
      }
      const entries = [...counts.entries()].sort(
        (a, b) => b[1] - a[1] || compareDisplay(a[0], b[0]),
      )
      const items = entries.slice(0, SUGGESTION_LIMIT).map(([value, count]) => ({ value, count }))
      // "…" if more distinct matches than shown, or the data source still has more pages.
      const hasMore = entries.length > SUGGESTION_LIMIT || st.hasMore
      return { items, hasMore }
    },
    [],
  )

  const openSuggestions = useCallback((key: string) => {
    clearTimeout(hideTimer.current)
    stateRef.current.suggestKey = key
    bump()
  }, [])

  // Toggle on click: open, then close on the next click of the same field.
  const toggleSuggestions = useCallback(
    (key: string) => {
      if (stateRef.current.suggestKey === key) closeSuggestions()
      else openSuggestions(key)
    },
    [closeSuggestions, openSuggestions],
  )

  const onFilterChange = useCallback(
    (key: string, value: string) => {
      stateRef.current.filtersRaw[key] = value
      stateRef.current.suggestKey = key // typing opens/refreshes the popup
      bump()
      reloadDebounced()
    },
    [reloadDebounced],
  )

  const clearFilter = useCallback(
    (key: string) => {
      stateRef.current.filtersRaw[key] = ''
      closeSuggestions()
      reloadImmediate()
      filterInputRefs.current[key]?.focus()
    },
    [closeSuggestions, reloadImmediate],
  )

  const pickSuggestion = useCallback(
    (key: string, value: string) => {
      stateRef.current.filtersRaw[key] = `=${value}` // convert the pick into an exact-match filter
      closeSuggestions()
      reloadImmediate()
    },
    [closeSuggestions, reloadImmediate],
  )

  const onGlobalChange = useCallback(
    (value: string) => {
      stateRef.current.globalRaw = value
      bump()
      reloadDebounced()
    },
    [reloadDebounced],
  )

  const clearGlobal = useCallback(() => {
    stateRef.current.globalRaw = ''
    bump()
    reloadImmediate()
  }, [reloadImmediate])

  const onScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      closeSuggestions() // the popup is fixed-positioned; hide it while scrolling
      const el = e.currentTarget
      const st = stateRef.current
      if (st.loading || !st.hasMore) return
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_THRESHOLD_PX) {
        loadPage(false)
      }
    },
    [closeSuggestions, loadPage],
  )

  const st = stateRef.current
  const dataColCount = columns.length
  const colWidths = columns.map((c) => c.width ?? DEFAULT_COL_WIDTH)
  const tableMinWidth = colWidths.reduce((sum, w) => sum + w, 0)
  const selectedRow = st.rows.find((r) => getRowId(r) === st.selectedId) ?? null
  const suggestion =
    st.suggestKey !== null ? { key: st.suggestKey, ...computeSuggestions(st.suggestKey) } : null
  const suggestAnchor = suggestion ? filterInputRefs.current[suggestion.key] : null

  return (
    <div
      className={`flex h-full w-full flex-col overflow-hidden bg-white text-[13px] text-[#1b2733] ${className ?? ''}`}
      role="region"
      aria-label={title ?? `${recordNoun} list`}
      // Escape bubbling up here wasn't consumed by clearing a non-empty filter/search field (those
      // stop propagation), so hand it to the host — e.g. to close a modal.
      onKeyDown={(e) => {
        if (e.key === 'Escape') onEscape?.()
      }}
    >
      {/* Header bar: title (left) + global search (centered). Omitted entirely in embedded mode
          (no title, global search off). */}
      {(title !== undefined || showGlobalSearch) && (
        <div className="grid shrink-0 grid-cols-[1fr_minmax(280px,520px)_1fr] items-center gap-4 border-b border-[#e2e6ea] px-5 py-2">
          {title !== undefined ? (
            <h1 className="m-0 text-[20px] font-semibold tracking-[-0.01em] whitespace-nowrap">
              {title}
            </h1>
          ) : (
            <div />
          )}
          {showGlobalSearch ? (
            <div className="relative">
              <svg
                className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-[#64748b]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                value={st.globalRaw}
                onChange={(e) => onGlobalChange(e.target.value)}
                onKeyDown={(e) => {
                  // Escape clears a non-empty global search (and is consumed so a host modal stays open);
                  // an empty field lets Escape bubble so the host can close.
                  if (e.key !== 'Escape') return
                  if (st.globalRaw !== '') {
                    e.stopPropagation()
                    clearGlobal()
                  }
                }}
                placeholder={globalSearchPlaceholder}
                autoComplete="off"
                aria-label={globalSearchPlaceholder}
                className="w-full rounded-lg border border-[#cdd4da] bg-[#f4f6f8] py-2.5 pr-8 pl-9 text-sm outline-none focus:border-[#2563eb] focus:bg-white focus:shadow-[0_0_0_3px_#eaf1ff]"
              />
              {st.globalRaw && (
                <ClearButton
                  onClear={() => {
                    clearGlobal()
                  }}
                />
              )}
            </div>
          ) : (
            <div />
          )}
          <div />
        </div>
      )}

      {/* Selection indicator — only once a record is chosen. */}
      {showSelectionBar && selectedRow && (
        <div className="flex shrink-0 items-center gap-2.5 border-b border-[#e2e6ea] bg-[#eaf1ff] px-5 py-2.5 text-[#1e40af]">
          <span className="inline-flex h-[18px] w-[18px] flex-none items-center justify-center rounded-full bg-[#2563eb] text-[11px] text-white">
            ✓
          </span>
          <span>
            {renderSelectionSummary ? (
              renderSelectionSummary(selectedRow)
            ) : (
              <>
                Selected:{' '}
                <strong className="font-semibold">
                  {columns.map((c) => displayOf(c, selectedRow)).find((v) => v) ?? ''}
                </strong>
              </>
            )}
          </span>
        </div>
      )}

      {/* Table */}
      <div onScroll={onScroll} className="flex-1 overflow-auto">
        <table
          className="w-full table-fixed border-collapse text-[13px]"
          style={{ minWidth: tableMinWidth }}
        >
          <colgroup>
            {columns.map((col, i) => (
              <col key={String(col.key)} style={{ width: colWidths[i] }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {columns.map((col) => {
                const sorted = st.sortKey === col.key
                const sortable = col.sortable !== false
                return (
                  <th
                    key={String(col.key)}
                    className="sticky top-0 z-20 border-b border-[#e2e6ea] bg-[#f8fafc] p-0 text-left align-bottom font-semibold whitespace-nowrap text-[#64748b]"
                  >
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() => toggleSort(col.key)}
                        className={`flex w-full min-w-0 cursor-pointer items-center gap-1.5 border-none bg-transparent px-3 py-[11px] text-[11px] tracking-[0.04em] uppercase select-none ${
                          sorted ? 'text-[#1b2733]' : 'hover:text-[#1b2733]'
                        }`}
                      >
                        <span className="truncate">{col.header}</span>
                        <span className="text-[10px] text-[#2563eb]">
                          {sorted ? (st.sortDir === 1 ? '▲' : '▼') : ''}
                        </span>
                      </button>
                    ) : (
                      <span className="block truncate px-3 py-[11px] text-[11px] tracking-[0.04em] uppercase">
                        {col.header}
                      </span>
                    )}
                  </th>
                )
              })}
            </tr>
            <tr>
              {columns.map((col) => {
                const key = String(col.key)
                const raw = st.filtersRaw[key] ?? ''
                return (
                  <th
                    key={key}
                    className="sticky z-10 border-b border-[#e2e6ea] bg-[#f8fafc] px-2 py-1.5"
                    style={{ top: HEADER_ROW_HEIGHT }}
                  >
                    {col.filterable !== false && (
                      <div className="relative">
                        <input
                          ref={(el) => {
                            filterInputRefs.current[key] = el
                          }}
                          type="text"
                          value={raw}
                          placeholder="Filter…"
                          autoComplete="off"
                          title="Prefix with = exact, ! not exact, > greater than, < less than; otherwise contains"
                          aria-label={`Filter ${col.header}`}
                          onChange={(e) => onFilterChange(key, e.target.value)}
                          onClick={() => toggleSuggestions(key)}
                          onBlur={() => {
                            hideTimer.current = setTimeout(() => closeSuggestions(), 120)
                          }}
                          onKeyDown={(e) => {
                            // Progressive Escape (each step consumes the event so a host modal stays
                            // open): close this field's open popup → else clear a non-empty filter →
                            // else (empty, popup hidden) let it bubble to onEscape.
                            if (e.key !== 'Escape') return
                            if (st.suggestKey === key) {
                              e.stopPropagation()
                              closeSuggestions()
                            } else if (raw !== '') {
                              e.stopPropagation()
                              clearFilter(key)
                            }
                          }}
                          className="w-full min-w-0 rounded-md border border-[#cdd4da] bg-white py-1 pr-6 pl-2 text-xs outline-none focus:border-[#2563eb] focus:shadow-[0_0_0_2px_#eaf1ff]"
                        />
                        {raw && <ClearButton onClear={() => clearFilter(key)} />}
                      </div>
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {st.rows.map((row) => {
              const id = getRowId(row)
              const isSelected = id === st.selectedId
              return (
                <tr
                  key={id}
                  onClick={() => select(row)}
                  className={`cursor-pointer border-b border-[#e2e6ea] ${
                    isSelected ? 'bg-[#e6f0ff] hover:bg-[#dbe8ff]' : 'hover:bg-[#f6f9ff]'
                  }`}
                >
                  {columns.map((col) => (
                    <td
                      key={String(col.key)}
                      className={`px-3 py-2.5 align-middle ${
                        col.wrap
                          ? 'break-words whitespace-normal'
                          : 'overflow-hidden text-ellipsis whitespace-nowrap'
                      }`}
                    >
                      {col.render ? col.render(row) : displayOf(col, row)}
                    </td>
                  ))}
                </tr>
              )
            })}
            {(st.loading || st.hasMore || st.error) && (
              <tr>
                <td
                  colSpan={dataColCount}
                  className="px-3 py-3.5 text-center text-xs text-[#64748b]"
                >
                  {st.error ? 'Failed to load.' : st.loading ? 'Loading…' : 'Scroll to load more…'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {!st.loading && !st.error && st.rows.length === 0 && (
          <div className="px-5 py-12 text-center text-sm text-[#64748b]">
            No {recordNoun}s match the current search or filters.
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="grid shrink-0 grid-cols-[1fr_auto_1fr] items-center gap-4 border-t border-[#e2e6ea] px-5 py-3 text-xs text-[#64748b]">
        <span className="justify-self-start">
          {st.loading && st.rows.length === 0
            ? 'Loading…'
            : `${st.rows.length} of ${st.total}${st.incompleteTotal ? '+' : ''} loaded`}
        </span>
        <span className="justify-self-center text-center">
          {footerHint === undefined ? `Click a row to select a ${recordNoun}.` : footerHint}
        </span>
        <span className="justify-self-end text-right">
          <Legend k="=" /> exact · <Legend k="!" /> not exact · <Legend k=">" /> greater ·{' '}
          <Legend k="<" /> less · no prefix = contains
        </span>
      </div>

      {suggestion && suggestAnchor && suggestion.items.length > 0 && (
        <SuggestionPopup
          anchor={suggestAnchor}
          items={suggestion.items}
          partial={st.hasMore}
          hasMore={suggestion.hasMore}
          onPick={(value) => pickSuggestion(suggestion.key, value)}
        />
      )}
    </div>
  )
}

function ClearButton({ onClear }: { onClear: () => void }) {
  return (
    <button
      type="button"
      aria-label="Clear"
      // mousedown (not click) fires before the input's blur, so focus is kept.
      onMouseDown={(e) => {
        e.preventDefault()
        onClear()
      }}
      className="absolute top-1/2 right-1.5 flex h-[15px] w-[15px] -translate-y-1/2 items-center justify-center rounded-full border-none bg-[#cdd4da] text-[11px] leading-none text-white hover:bg-[#64748b]"
    >
      ×
    </button>
  )
}

function Legend({ k }: { k: string }) {
  return (
    <code className="rounded border border-[#e2e6ea] bg-[#f4f6f8] px-1.5 py-px text-[11px] text-[#1b2733]">
      {k}
    </code>
  )
}
